import { useState } from "react";
import { Activity, AlertCircle, Loader2 } from "lucide-react";
import { authClient } from "@corpuscomplete/web/auth/client";
import { Env } from "@corpuscomplete/web/env";
import { Route } from "@corpuscomplete/web/routes/_unauthenticated/login";
import { Alert, AlertDescription, AlertTitle } from "@corpuscomplete/web-ui/components/alert";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@corpuscomplete/web-ui/components/card";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { Text } from "@corpuscomplete/web-ui/components/text";

function AppleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GoogleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function LoginPage() {
  const search = Route.useSearch();
  const [isSubmitting, setIsSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [devEmail, setDevEmail] = useState("");

  const handleSignIn = async (provider: "google" | "apple") => {
    setIsSubmitting(provider);
    setError(null);
    try {
      await authClient.signIn.social({
        provider,
        callbackURL: `${Env.VITE_WEB_APP_URL}${search.redirect}`,
      });
    } catch (error) {
      console.error("Sign in failed", error);
      setError("Failed to sign in. Please try again.");
      setIsSubmitting(null);
    }
  };

  const handleDevLogin = async (email?: string) => {
    setIsSubmitting("dev");
    setError(null);
    try {
      const trimmedEmail = email?.trim();

      const res = await fetch(
        `${Env.VITE_API_URL}/dev/login`,
        trimmedEmail
          ? {
              method: "POST",
              credentials: "include",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ email: trimmedEmail }),
            }
          : {
              method: "POST",
              credentials: "include",
            },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Dev login failed (${res.status})`);
      }
      window.location.href = search.redirect ?? "/home";
    } catch (error) {
      console.error("Dev login failed", error);
      setError("Dev login failed. Check API dev login is enabled.");
      setIsSubmitting(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mb-2 flex justify-center">
            <div className="rounded-full bg-primary/10 p-3">
              <Activity className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Corpus Complete</CardTitle>
          <CardDescription>Sign in to access your nutrition and fitness tracking</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="size-3.5" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <Button
              variant="outline"
              onClick={() => handleSignIn("apple")}
              disabled={!!isSubmitting}
              className="h-10 w-full bg-background hover:bg-accent hover:text-accent-foreground"
            >
              <div className="flex w-full items-center justify-center gap-2">
                {isSubmitting === "apple" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <AppleIcon className="mr-2 h-4 w-4" />
                )}
                <Text variant="sm" className="font-medium">
                  {isSubmitting === "apple" ? "Signing in..." : "Sign in with Apple"}
                </Text>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSignIn("google")}
              disabled={!!isSubmitting}
              className="h-10 w-full bg-background hover:bg-accent hover:text-accent-foreground"
            >
              <div className="flex w-full items-center justify-center gap-2">
                {isSubmitting === "google" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GoogleIcon className="mr-2 h-4 w-4" />
                )}
                <Text variant="sm" className="font-medium">
                  {isSubmitting === "google" ? "Signing in..." : "Sign in with Google"}
                </Text>
              </div>
            </Button>

            {import.meta.env.DEV && (
              <div className="grid gap-2 pt-1">
                <div className="grid gap-1.5">
                  <Label className="text-xs text-muted-foreground">Dev email (optional)</Label>
                  <Input
                    value={devEmail}
                    onChange={(e) => setDevEmail(e.target.value)}
                    placeholder="user2@localhost"
                    disabled={!!isSubmitting}
                    className="h-9"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                </div>
                <Button
                  variant="ghost"
                  onClick={() => handleDevLogin(devEmail)}
                  disabled={!!isSubmitting}
                  className="h-9 w-full"
                >
                  <Text variant="xs" className="font-medium">
                    {isSubmitting === "dev" ? "Signing in..." : "Dev Login"}
                  </Text>
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
